import {useEffect, useState} from "react";
import {makeStyles} from '@material-ui/styles';
import { Button, Container, Grid, Typography } from "@material-ui/core";
import ProductItem from "../../../products/item";
import {useTranslation} from "tools/i18n";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import theme from "../../../theme";
import urlNormalizer from "tools/urlNormalization";
import {useRouter} from "next/router";
import { elevateClickNotification, elevateProductPage } from "tools/elevateClient";
import { getCategoryBreadcrumb, getMainProduct } from '@functions/productOps';
import {
	getDeliveryTimeId,
	getFastDelivery,
	getMainVariant,
	getStock,
	getUUID,
} from '@functions/elevateOps';
import useSessionStore from '@services/stores/SessionStore';
import ProductThumbnail from '@components/products/thumbnail';

const useStyles = makeStyles(theme => ({
	outerWrapper: {
		display: 'flex',
		flexDirection: 'column',
		marginTop: 20,
		marginBottom: 20
	},
	wrapper: {
		marginTop: 20,
		marginBottom: 40,
	},
	title: {
		...theme.typography.h2,
		marginBottom: 10,
		textAlign: 'center'
	},
	grid: {
        [theme.breakpoints.down('md')]: {
            width: '100%',
            margin: 0
        }
    },
	showMore: {
	    minWidth: 200,
        marginTop: 20,
        padding: '10px 24px',
        backgroundColor: theme.palette.primary.color,
        fontSize: 11,
        color: theme.palette.primary.background,
        fontWeight: 600,
        letterSpacing: 1,
        borderRadius: 0,
        '&:hover': {
	        backgroundColor: theme.palette.primary.color
        },
		'.xmas &': {
			backgroundColor: theme.palette.primary.main,
		}
    },
}), {name: 'RelatedProducts'});

export default function ElevateRelated({product, configurator, breadcrumbs}) {
	const classes = useStyles();
	const router = useRouter();
	const [productSlots, setProductSlots] = useState([]);
	const [pagination, setPagination] = useState({
		perPage: 4,
		page: {
			'PDP-1': 1,
			'PDP-2': 1,
			'PDP-3': 1
		}
	});

	const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const config = useSessionStore((state) => state.session.config);
	const {t} = useTranslation();
	const titles = {
		'PDP-1': 'related-title',
		'PDP-2': 'also-bought-title',
		'PDP-3': 'recently-viewed-title'
	}

	useEffect(async () => {
		try {
			const searchResults = await elevateProductPage(
				'PRD-' + getMainProduct(product, configurator).productNumber,
				config.elevateMarket,
				config.language.languageCode,
				isMobile ? 'mobile' : 'desktop'
			);

			let slots = searchResults.recommendationLists.map((slot) => {
				let products = [];
				slot.productGroups.forEach((productGroup) => {
					products.push(productGroup.products[0]);
				});

				return {
					slot: slot.id,
					products
				}
			});

			setProductSlots(slots);
		} catch (e) {
			console.log('Cant find recommendations for product with SKU: ', product.productNumber);
		}
	}, [product.productNumber]);

	async function onClick(product) {
		await elevateClickNotification(
			product.ticket,
			config.elevateMarket,
			config.language.languageCode,
			isMobile ? 'mobile' : 'desktop'
		)
	}

	function urlNormalize(url) {
		return urlNormalizer(url, router.basePath);
	}

	function getMarketingLabel(product) {
		if(!product?.custom?.marketing_label) {
			return;
		}

		return product?.custom?.marketing_label;
	}

	function loadMore(slot) {
        setPagination((prevState) => {
			let state = {...prevState};
			state.page[slot] = state.page[slot] + 1;

			return state;
		});
    }


	function getProductVariants(product) {
		let allVariants = product.variants.filter((variant) => variant.label).map((variant) => {
			return variant.label;
		});

		return allVariants.length > 0;
	}

	function renderProduct(product, key) {
		let mainVariant = getMainVariant(product);

		return <ProductItem
			id={getUUID(mainVariant)}
			productNumber={mainVariant.key}
			name={product.title}
			manufacturer={product.brand}
			link={urlNormalize(product.link)}
			prices={{
				price: mainVariant.sellingPrice,
				listPrice: mainVariant.listPrice,
				isOnSale: mainVariant.sellingPrice < mainVariant.listPrice,
				discount: mainVariant.sellingPrice < mainVariant.listPrice ? {
					amount: mainVariant.sellingPrice - mainVariant.listPrice,
					percentage: preventFloatRoundingIssues(mainVariant.sellingPrice, mainVariant.listPrice)
				} : undefined
			}}
			image={<ProductThumbnail product={{id: getUUID(mainVariant)}}/> }
			list="Elevate recommendations"
			categoryBreadcrumb={getCategoryBreadcrumb(breadcrumbs)}
			variants={getProductVariants(product) ? mainVariant.label : false}
			stock={getStock(mainVariant)}
			fastDelivery={getFastDelivery(mainVariant)}
			labels={getMarketingLabel(mainVariant)}
			available={product.inStock}
			clickCallback={onClick.bind(this, product)}
			elevateTicket={product.ticket}
			isMobile={isMobile}
			addToCart={'minimal'}
			isOutlet={product.title.indexOf('OUTLET') >= 0}
			lazyHydrate={false}
			position={key + 1}
			key={key}
			deliveryTimeString={getDeliveryTimeId(product)}
		/>
	}

	function preventFloatRoundingIssues(price, listPrice) {
		let centPrice = parseInt(price * 100);
		let centListPrice = parseInt(listPrice * 100);

		return parseInt(Math.round(100 * (centListPrice - centPrice) / centListPrice));
	}

	return (
		<div className={classes.outerWrapper}>
			{productSlots.map((slot, key) => {
				let title = titles[slot.slot];
				let page = pagination.page[slot.slot];

				if(slot.products.length <= 0) {
					return;
				}

				return (
					<div className={classes.wrapper} key={key}>
						<Container disableGutters>
							<Typography variant="h2" className={classes.title}>{t(title)}</Typography>
							<Grid
								container
								justify="flex-start"
								alignItems="stretch"
								spacing={!isDesktop ? 1 : 2}
								className={classes.grid}
							>
								{slot.products.length > 0 ? slot.products.map((product, key) => pagination.perPage * page > key ? renderProduct(product, key) : '') : ''}
							</Grid>

							<Grid justify="center" container>
								{slot.products.length > pagination.perPage * page ? <Button className={classes.showMore} onClick={loadMore.bind(this, slot.slot)}>{t('show-more')}</Button> : ''}
							</Grid>
						</Container>
					</div>
				)
			})}
		</div>
	)
}
